import { FunctionComponent, ReactNode } from "react";

import { faCheckCircle, faCircle, faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Spinner } from "@/components/Spinner/Spinner";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { FitLevel } from "@/enum/fit-level.enum";

interface DeepResearchStatusIconProps {
	isDone: boolean;
	isRunning: boolean;
	/** Waiting for a slot, not yet handed to a worker (PREC-4052). */
	isQueued?: boolean;
	/** The last run failed; shown over an older result, which it did not replace (PREC-4052). */
	isFailed?: boolean;
	fitAssessment?: string | null;
	tooltipText: string;
	onClick?: () => void;
}

const FIT_CONFIG: Record<FitLevel, { label: string; bgClass: string; textClass: string }> = {
	[FitLevel.STRONG]: {
		label: "S",
		bgClass: "bg-status-success-light",
		textClass: "text-status-success",
	},
	[FitLevel.MEDIUM]: {
		label: "M",
		bgClass: "bg-status-warning-light",
		textClass: "text-status-warning",
	},
	[FitLevel.WEAK]: {
		label: "W",
		bgClass: "bg-status-error-light",
		textClass: "text-status-error",
	},
	[FitLevel.NO_FIT]: {
		label: "N",
		bgClass: "bg-status-error-light",
		textClass: "text-status-error",
	},
};

interface IconState {
	isDone: boolean;
	isRunning: boolean;
	isQueued: boolean;
	isFailed: boolean;
	fitAssessment?: string | null;
}

const resolveIcon = ({
	isDone,
	isRunning,
	isQueued,
	isFailed,
	fitAssessment,
}: IconState): ReactNode => {
	// A queued or running research always wins, even over an existing (older) result during a rerun.
	if (isQueued) {
		return (
			<span
				className="h-2.5 w-2.5 animate-pulse rounded-full bg-text-tertiary"
				data-testid="deep-research-queued"
			/>
		);
	}

	if (isRunning) {
		return <Spinner color="#4d66f5" />;
	}

	if (isFailed) {
		return (
			<Icon
				className="h-[16px] text-status-error"
				data-testid="deep-research-failed"
				icon={faCircleExclamation}
			/>
		);
	}

	if (isDone && fitAssessment && Object.values(FitLevel).includes(fitAssessment as FitLevel)) {
		const cfg = FIT_CONFIG[fitAssessment as FitLevel];

		return (
			<span
				className={`inline-flex items-center justify-center w-[22px] h-[22px] rounded-full text-xs font-bold ${cfg.bgClass} ${cfg.textClass}`}
			>
				{cfg.label}
			</span>
		);
	}

	if (isDone) {
		return <Icon className="text-green-500 h-[16px]" icon={faCheckCircle} />;
	}

	return <Icon className="text-gray-400 h-[16px]" icon={faCircle} />;
};

export const DeepResearchStatusIcon: FunctionComponent<DeepResearchStatusIconProps> = ({
	isDone,
	isRunning,
	isQueued = false,
	isFailed = false,
	fitAssessment,
	tooltipText,
	onClick,
}) => {
	return (
		<Tooltip content={tooltipText}>
			<div
				className="flex items-center justify-center cursor-pointer"
				onClick={(e) => {
					e.stopPropagation();
					onClick?.();
				}}
			>
				{resolveIcon({ isDone, isRunning, isQueued, isFailed, fitAssessment })}
			</div>
		</Tooltip>
	);
};
